import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { nominalBilangan as currencyFormatter } from "utils/currencyFormatter";
import CardListingDetailBuyerArchived from "components/molecules/buyer-match/card-listing-detail-buyer-archived";
import CardListingDetailBuyer from "components/molecules/buyer-match/card-listing-detail-buyer";

import IEmpty from "assets/empty-state-listing-match.png";
import IEmptyArchived from "assets/empty-state-list-archived.png";

const FEAT = ({ val }) => {
  const _getQubic = () => {
    if (val.includes("LT") || val.includes("LB"))
      return (
        <span>
          m<sup>2</sup>
        </span>
      );
    return "";
  };
  return (
    <span className="text-[11px] tracking-[-0.4px] leading-[18.571px] px-2 py-0.5 bg-white rounded-[4px] font-inter text-[#808285]">
      {val} {_getQubic()}
    </span>
  );
};

const ListListingDetailBuyerMatch = ({
  detailCustomer,
  listListingCustomer,
  status,
  refetchListListingCustomer,
  listCustomerParams,
  setListCustomerParams,
  fetchNextPage,
  hasNextPage,
  isSearch,
}) => {
  const { pathname } = useLocation();
  const isArchived = pathname.includes("list-archived");

  const observer = useRef(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const DATA_LOKASI = useMemo(() => {
    return detailCustomer?.preferred_areas ?? [];
  }, [detailCustomer]);

  const DATA_FEATURE = useMemo(() => {
    if (!detailCustomer) return [];

    const temp = [];

    if (detailCustomer?.bedroom) temp.push(`KT. ${detailCustomer?.bedroom}`);
    if (detailCustomer?.bathroom) temp.push(`KM. ${detailCustomer?.bathroom}`);
    if (detailCustomer?.min_surface_area)
      temp.push(`LT. ${detailCustomer?.min_surface_area}`);
    if (detailCustomer?.min_building_area)
      temp.push(`LB. ${detailCustomer?.min_building_area}`);

    if (detailCustomer?.min_price && detailCustomer?.max_price)
      temp.push(
        `Rp${currencyFormatter(
          detailCustomer?.min_price
        )} - Rp${currencyFormatter(detailCustomer?.max_price)}`
      );
    return temp;
  }, [detailCustomer]);

  const TextEmpty = useMemo(() => {
    const res = {
      title: "",
      subtitle: "",
      icon: isArchived ? IEmptyArchived : IEmpty,
    };
   
    if (status === "matched") {
      res.title = "Belum ada saran listing";
      res.subtitle = "Listing yang sesuai dengan preferensi customer akan ditampilkan di sini.";
    }
    if (status === "sent") {
      res.title = "Belum Ada Listing Terkirim";
      res.subtitle = "Kirim listing yang relevan kepada customer untuk menampilkannya di sini.";
    }
    if (status === "interested" && !isArchived) {
      res.title = "Tidak Ada Listing yang Diminati";
      res.subtitle = "Listing yang diminati customer akan ditampilkan di sini setelah Anda mengubah responnya menjadi Tertarik.";
    }
    if (status === "interested" && isArchived) {
      res.title = "Belum Ada yang Tertarik";
      res.subtitle = "Listing yang disukai customer akan ditampilkan di sini.";
    }
    if (status === "not_interested") {
      if(isArchived){
        res.title = "Tidak Ada yang Tidak Tertarik";
        res.subtitle = "Listing yang tidak diminati customer akan ditampilkan di sini.";
      } else {
        res.title = "Tidak Ada Listing yang Ditolak";
        res.subtitle = "Listing yang tidak diminati customer akan ditampilkan di sini setelah Anda mengubah responnya menjadi Tidak Tertarik.";
      }
    }

    if (isSearch) {
      res.title = "Listing tidak ditemukan";
      res.subtitle = "Periksa kembali ejaan judul atau ID listing.";
    }

    return res;
  }, [status, isArchived, isSearch]);

  const handleLoadMore = useCallback(async () => {
    if (isFetchingMore) return;

    // No pagination handlers provided
    if (!fetchNextPage && (!setListCustomerParams || !refetchListListingCustomer)) return;

    setIsFetchingMore(true);

    try {
      if (fetchNextPage) {
        await fetchNextPage();
      } else if (setListCustomerParams && refetchListListingCustomer) {
        setListCustomerParams((prev) => {
          const currentPage = prev?.page || 1;
          return {
            ...prev,
            page: currentPage + 1,
          };
        });
        await refetchListListingCustomer();
      }
    } finally {
      setIsFetchingMore(false);
    }
  }, [fetchNextPage, isFetchingMore, refetchListListingCustomer, setListCustomerParams]);

  const lastItemRef = useCallback(
    (node) => {
      if (!listListingCustomer || !listListingCustomer.length) return;
      if (!fetchNextPage && (!setListCustomerParams || !refetchListListingCustomer)) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          if (hasNextPage === false) return;
          handleLoadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [
      handleLoadMore,
      listListingCustomer,
      refetchListListingCustomer,
      setListCustomerParams,
      fetchNextPage,
      hasNextPage,
    ]
  );

  return (
    <div className="rounded-[4px] border-[0.5px] border-solid border-[#D8D8D9]">
      {/* HEADER */}
      <div className="px-4 bg-[#FAFAFA] flex items-center gap-2">
        <div className="flex items-center gap-2 py-2">
          <span className="text-[11px] tracking-[-0.4px] leading-[18.571px] font-inter text-[#808285]">
            Preferensi:{" "}
          </span>
          <span className="text-[11px] tracking-[-0.4px] leading-[18.571px] px-2 py-0.5 bg-white rounded-[4px] font-inter text-[#808285]">
            {detailCustomer?.listing_type?.name ?? "-"}
          </span>
          <span className="text-[11px] tracking-[-0.4px] leading-[18.571px] px-2 py-0.5 bg-white rounded-[4px] font-inter text-[#808285]">
            {detailCustomer?.property_type?.name ?? "-"}
          </span>
        </div>

        <div className="h-[30px] w-[0.5px] bg-[#D8D8D9]" />

        {!!DATA_LOKASI?.length && (
          <div className="flex items-center gap-2 py-2">
            {DATA_LOKASI?.filter((_, index) => index < 3)?.map(
              (item, index) => (
                <span
                  key={`item-data-lokasi-${index}`}
                  className="text-[11px] tracking-[-0.4px] leading-[18.571px] px-2 py-0.5 bg-white rounded-[4px] font-inter text-[#808285]"
                >
                  {item?.name}
                </span>
              )
            )}
            {DATA_LOKASI?.length > 3 && (
              <span className="text-[11px] tracking-[-0.4px] leading-[18.571px] px-2 py-0.5 bg-[#FAFAFA] rounded-[4px] font-inter text-[#D8D8D9]">
                +{DATA_LOKASI?.length - 3} lokasi lainnya
              </span>
            )}
          </div>
        )}

        <div className="h-[30px] w-[0.5px] bg-[#D8D8D9]" />

        <div className="flex items-center gap-2 py-2">
          {DATA_FEATURE?.map((item, index) => (
            <FEAT key={`item-data-fitur-${index}`} val={item} />
          ))}
        </div>
      </div>

      {/* CONTENT */}
      <div>
        {!!listListingCustomer?.length ? (
          listListingCustomer?.map((item, index) => {
            const isLastItem = index === listListingCustomer.length - 1;

            return (
              <div
                key={
                  isArchived
                    ? `item-listing-detail-buyer-archived-${index}`
                    : `item-listing-detail-buyer-${index}`
                }
                ref={isLastItem ? lastItemRef : undefined}
              >
                {isArchived ? (
                  <CardListingDetailBuyerArchived
                    {...item}
                    refetchListListingCustomer={refetchListListingCustomer}
                  />
                ) : (
                  <CardListingDetailBuyer
                    {...item}
                    refetchListListingCustomer={refetchListListingCustomer}
                  />
                )}
              </div>
            );
          })
        ) : (
          <div className="h-[494px] flex flex-col items-center justify-center gap-0.5 py-6">
            <img
              className="w-[48px] h-[48px] object-contain"
              src={TextEmpty.icon}
              alt="empty-state"
            />
            <p className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter-semibold text-[#252526]">
              {TextEmpty.title}
            </p>
            <p className="text-[11px] leading-[17.798px] tracking-[-0.4px] font-inter text-[#808285]">
              {TextEmpty.subtitle}
            </p>
          </div>
        )}

        {isFetchingMore && (
          <div className="flex justify-center items-center py-3">
            <div className="w-4 h-4 border-2 border-[#D8D8D9] border-t-[#252526] rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ListListingDetailBuyerMatch;
